import { AggregationFunction } from "@multiweb/clients/dataReader";
import { ColorIntensity, DataAttributesProps, ReportingScalarProps } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { loadLocalizationIfNeeded } from "@multiweb/reactCore/i18n";
import { FlagColor } from "@multiweb/types/react";
import { EventCallback } from "Global/Base/Events";
import { IDisposable } from "Global/Base/IDisposable";
import { EnumUtils } from "Global/Base/Utils/EnumUtils";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { createClearAction, createInitAction } from "UI/UI.React/components/ReportingScalar/action";
import { ControlUtils } from "UI/UI/UI/Controls/ControlUtils";
import { ControlConnectorWithActualObject } from "../ControlConnectorWithActualObject";
import { GenericControlConnectorAttrs } from "../GenericControlConnector";

const { dispatchAction, stringToEnumValue, generateUUID } = bridge;

export const REPORTIN_SCALAR_DATA_FORMAT_FNC = "numeralDataFormat";

export class ReportingScalarConnector extends ControlConnectorWithActualObject implements IDisposable {
    /** agregační funkce */
    private _aggregationFunc: AggregationFunction;
    /** jméno atributu pro načtení agregované hodnoty */
    private _attrName: string;
    /** Barva pozadí */
    private _backgroundColor: FlagColor;
    /** Nadpis  */
    private _caption: string;
    /** název třídy */
    private _className: string;
    /** intenzita barvy */
    private _colorIntensity: ColorIntensity;
    /** Podmínka pro načtení dat */
    private _condition: string;
    /** Parametry podmínky */
    private _conditionParams: string[];
    /** unikátní identifikační klíč */
    private _dataKey: string;
    /** Popis */
    private _description: string;
    /** Formát, ve kterém se má zobrazit výsledná hodnota */
    private _displayFormat: string;
    /** Název formátovací funkce */
    private _formatProviderKey: string;
    /** typ dataEventu */
    private _eventName: string;
    /** Zdroj dataEventu */
    private _eventSource: string;
    /** název třídy z dataEvents */
    private _subEventName: string;
    /** Formulář otevřený při kliku */
    private _formName: string;
    /** Název vazby na nadřízený objekt */
    private _masterLinkToAttr: string;
    /** Statická metoda pro načtení dat */
    private _methodName: string;
    /** Lokalizační klíč popisu */
    private _secondaryLC: string;
    private _listener: EventCallback;

    constructor(attrs: GenericControlConnectorAttrs, canRender: boolean = false) {
        super(attrs, false);

        const conditionParams = ControlUtils.getDataAttr("conditionParams", this.element);

        this._aggregationFunc = this._getAggregatefunction();
        this._attrName = ControlUtils.getDataAttr("attrname", this.element);
        this._backgroundColor = (ControlUtils.getDataAttr("backgroundcolor", this.element) || "none") as FlagColor;
        this._caption = ControlUtils.getDataAttr("caption", this.element);
        this._className = ControlUtils.getDataAttr("objectname", this.element);
        this._colorIntensity = EnumUtils.getEnumValueFromName(ColorIntensity, ControlUtils.getDataAttr("colorintensity", this.element) || ColorIntensity.default, false);
        this._condition = ControlUtils.getDataAttr("condition", this.element);
        this._conditionParams = conditionParams ? conditionParams.split(";") : undefined;
        this._dataKey = generateUUID();
        this._description = ControlUtils.getDataAttr("description", this.element);
        this._displayFormat = ControlUtils.getDataAttr("displayformat", this.element);
        this._formatProviderKey = ControlUtils.getDataAttr("formatfunc", this.element) || REPORTIN_SCALAR_DATA_FORMAT_FNC;
        this._formName = ControlUtils.getDataAttr("formname", this.element);
        this._masterLinkToAttr = ControlUtils.getDataAttr("masterlinktoattr", this.element);
        this._methodName = ControlUtils.getDataAttr("methodname", this.element);
        this._secondaryLC = ControlUtils.getDataAttr("secondarylc", this.element);

        this._eventName = ControlUtils.getDataAttr("eventname", this.element) || "*";
        this._eventSource = ControlUtils.getDataAttr("eventsource", this.element) || "OW";
        this._subEventName = ControlUtils.getDataAttr("subeventname", this.element);

        loadLocalizationIfNeeded(this._locKey);
        loadLocalizationIfNeeded(this._secondaryLC);

        this._renderIfTrue(canRender);
    }

    public override get actualObject(): OWObject {
        return this._actualObject;
    }

    public override set actualObject(actualObject: OWObject) {
        this._removeActualObjectListener();
        super.actualObject = actualObject;

        actualObject?.attributesChanged.add(this._listener = () => {
            this._refresh();
        });

        this._refresh();
    }

    /** Get aggregate function from element. Takes into account different attribute in the x-gen and in the form designer. By bugfix 47618 */
    private _getAggregatefunction(): AggregationFunction {
        return stringToEnumValue(AggregationFunction, ControlUtils.getDataAttr("aggregatefunction", this.element))
            ?? stringToEnumValue(AggregationFunction, ControlUtils.getDataAttr("aggregationfunc", this.element));
    }

    private _refresh(): void {
        dispatchAction(createInitAction(
            this._aggregationFunc,
            this._attrName,
            this._className,
            this._condition,
            this._dataKey,
            this._displayFormat,
            this._formatProviderKey,
            this._conditionParams,
            this.actualObject?.instanceID,
            this._methodName,
        ));

        this._render();
    }

    protected override _getRenderProps(): ReportingScalarProps & DataAttributesProps {
        return {
            ...super._getRenderProps(),
            aggregationFunc: this._aggregationFunc,
            attrName: this._attrName,
            backgroundColor: this._backgroundColor,
            caption: this._caption,
            className: this._className,
            colorIntensity: this._colorIntensity,
            condition: this._condition,
            conditionParams: this._conditionParams,
            dataKey: this._dataKey,
            description: this._description,
            displayFormat: this._displayFormat,
            formatProviderKey: this._formatProviderKey,
            formName: this._formName,
            interactive: this.interactive,
            masterLinkToAttr: this._masterLinkToAttr,
            methodName: this._methodName,
            secondaryLC: this._secondaryLC,
            instanceId: this.actualObject?.instanceID,
            eventName: this._eventName,
            eventSource: this._eventSource,
            subEventName: this._subEventName,
        };
    }

    private _removeActualObjectListener(): void {
        if (super.actualObject && this._listener) {
            super.actualObject.attributesChanged.remove(this._listener);
            this._listener = null;
        }
    }

    public override dispose(): void {
        super.dispose();

        this._removeActualObjectListener();
        dispatchAction(createClearAction(this._dataKey));
    }
}
